import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '../ui/Modal';
import { useAuth } from '../../contexts/AuthContext';
import {
  canPlayCloudTalkRecordings,
  fetchCloudTalkCallInsightByCallId,
  generateCloudTalkCallAiFromRecording,
  upsertCloudTalkCallTranscriptSummary,
} from '../../lib/demCloudTalk';
import type { DemCloudTalkCallGroup, DemCloudTalkCallInsight } from '../../types/dem';
import { CloudTalkRecordingPlayer } from './CloudTalkRecordingPlayer';

type CloudTalkCallDetailModalProps = {
  group: DemCloudTalkCallGroup | null;
  open: boolean;
  onClose: () => void;
  /** Prefetched insight (e.g. from AI Insights list). */
  insight?: DemCloudTalkCallInsight | null;
};

function DetailField({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'default' | 'primary' | 'warn';
}) {
  return (
    <div className="dem-cloudtalk-detail-field">
      <span className="dem-cloudtalk-detail-field__label">{label}</span>
      <span
        className={`dem-cloudtalk-detail-field__value${tone ? ` dem-cloudtalk-detail-field__value--${tone}` : ''}`}
      >
        {value || '—'}
      </span>
    </div>
  );
}

function displaySummary(raw: string): string {
  const text = raw.trim();
  if (!text || /^array$/i.test(text)) return '';
  return text;
}

function insightHasAi(row: DemCloudTalkCallInsight | null): boolean {
  if (!row) return false;
  return Boolean(row.transcriptText?.trim() || displaySummary(row.summary ?? ''));
}

export function CloudTalkCallDetailModal({
  group,
  open,
  onClose,
  insight: insightProp = null,
}: CloudTalkCallDetailModalProps) {
  const { portalAccess } = useAuth();
  const canPlayRecording = canPlayCloudTalkRecordings(portalAccess);
  const [insight, setInsight] = useState<DemCloudTalkCallInsight | null>(insightProp);
  const [insightLoading, setInsightLoading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiModel, setAiModel] = useState('');
  const insightRef = useRef<DemCloudTalkCallInsight | null>(insightProp);
  const autoKeyRef = useRef('');
  const generateSeqRef = useRef(0);

  useEffect(() => {
    insightRef.current = insight;
  }, [insight]);

  const recordingCallIds = useMemo(() => {
    if (!group) return [] as string[];
    return group.callIds?.length > 0 ? group.callIds : group.callId ? [group.callId] : [];
  }, [group]);

  const primaryCallId = useMemo(() => {
    const numeric = recordingCallIds.find((id) => /^\d+$/.test(id));
    return numeric || '';
  }, [recordingCallIds]);

  const runGenerate = useCallback(
    async (opts: { force: boolean }) => {
      if (!group || !primaryCallId) return;
      if (!opts.force && insightHasAi(insightRef.current)) return;

      const seq = ++generateSeqRef.current;
      setAiBusy(true);
      setAiError(null);
      try {
        const result = await generateCloudTalkCallAiFromRecording(primaryCallId);
        if (seq !== generateSeqRef.current) return;
        setAiModel(result.model);
        const saved = await upsertCloudTalkCallTranscriptSummary({
          callId: primaryCallId,
          callUuid: insightRef.current?.callUuid,
          transcript: result.transcript,
          summary: displaySummary(result.summary) || result.summary,
          externalNumber: group.callerNumber,
          contactName: group.contactName,
          companyName: group.companyName,
          calledLine: group.calledLine,
          agentName: group.resolvedBy !== '—' ? group.resolvedBy : '',
        });
        if (seq !== generateSeqRef.current) return;
        setInsight(saved);
      } catch (e) {
        if (seq !== generateSeqRef.current) return;
        setAiError(e instanceof Error ? e.message : 'Failed to generate transcript / summary.');
      } finally {
        if (seq === generateSeqRef.current) setAiBusy(false);
      }
    },
    [group, primaryCallId],
  );

  useEffect(() => {
    if (!open || !group) {
      generateSeqRef.current += 1;
      autoKeyRef.current = '';
      setInsight(insightProp);
      setInsightLoading(false);
      setAiBusy(false);
      setAiError(null);
      setAiModel('');
      return;
    }

    let cancelled = false;
    setAiError(null);
    setAiModel('');

    const bootstrap = async () => {
      let row = insightProp;
      if (!insightProp) {
        setInsightLoading(true);
        try {
          row = await fetchCloudTalkCallInsightByCallId(group.callId);
        } catch {
          row = null;
        }
        if (cancelled) return;
        setInsight(row);
        setInsightLoading(false);
      } else {
        setInsight(insightProp);
        setInsightLoading(false);
      }

      if (cancelled || !canPlayRecording || !primaryCallId) return;
      if (insightHasAi(row)) return;

      const autoKey = `${group.groupKey}:${primaryCallId}`;
      if (autoKeyRef.current === autoKey) return;
      autoKeyRef.current = autoKey;
      await runGenerate({ force: false });
    };

    void bootstrap();
    return () => {
      cancelled = true;
      generateSeqRef.current += 1;
      // Allow a clean retry after Strict Mode remount / modal reopen.
      autoKeyRef.current = '';
    };
  }, [open, group, insightProp, canPlayRecording, primaryCallId, runGenerate]);

  if (!group) return null;

  const callTimes = group.callTimes.length > 1 ? group.callTimes.join(', ') : null;
  const transcript = insight?.transcriptText?.trim() || '';
  const summary = displaySummary(insight?.summary ?? '');
  const hasPortalAi = Boolean(transcript || summary);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Call detail: ${group.callerNumber}`}
      panelClassName="modal-panel--cloudtalk-detail"
    >
      <div className="dem-cloudtalk-detail dem-cloudtalk-detail--split">
        <div className="dem-cloudtalk-detail__col dem-cloudtalk-detail__col--meta">
          <h3 className="dem-cloudtalk-detail__ai-title">Call details</h3>
          <DetailField label="Name" value={group.contactName} />
          <DetailField label="Company" value={group.companyName} tone="primary" />
          <DetailField label="Number" value={group.callerNumber} />
          {callTimes ? <DetailField label="Call times" value={callTimes} tone="warn" /> : null}
          <DetailField label="Called line" value={group.calledLine} />
          <DetailField label="Status" value={group.status} />
          <DetailField label="Resolved by" value={group.resolvedBy || '—'} />
          <DetailField label="Tags" value={group.tags || 'No tags'} tone="warn" />

          <div className="dem-cloudtalk-detail__remarks">
            <span className="dem-cloudtalk-detail-field__label">Remarks / notes</span>
            <div className="dem-cloudtalk-detail__remarks-box">
              {group.remarks || 'No specific notes from CloudTalk.'}
            </div>
          </div>

          {canPlayRecording ? (
            <section className="dem-cloudtalk-detail__recording" aria-label="Call recording">
              <div className="dem-cloudtalk-detail__ai-head">
                <h3 className="dem-cloudtalk-detail__ai-title">Call recording</h3>
                <span className="dem-cloudtalk-dem-muted">Management / IT only</span>
              </div>
              <CloudTalkRecordingPlayer callIds={recordingCallIds} />
            </section>
          ) : null}
        </div>

        <section
          className="dem-cloudtalk-detail__col dem-cloudtalk-detail__col--ai"
          aria-label="Transcript and summary"
        >
          <div className="dem-cloudtalk-detail__ai-head">
            <h3 className="dem-cloudtalk-detail__ai-title">Transcript & summary</h3>
            {canPlayRecording && primaryCallId ? (
              <button
                type="button"
                className="btn-secondary btn-sm"
                disabled={aiBusy || insightLoading}
                onClick={() => {
                  autoKeyRef.current = `${group.groupKey}:${primaryCallId}:manual`;
                  void runGenerate({ force: true });
                }}
              >
                {aiBusy
                  ? 'Generating…'
                  : hasPortalAi
                    ? 'Regenerate'
                    : 'Generate from recording'}
              </button>
            ) : null}
          </div>

          {insightLoading ? (
            <p className="dem-cloudtalk-detail__ai-empty">Loading saved transcript…</p>
          ) : null}

          {aiBusy ? (
            <p className="dem-cloudtalk-detail__ai-empty">
              Generating transcript & summary from recording…
            </p>
          ) : null}

          {aiError ? <p className="dem-cloudtalk-detail__ai-empty">{aiError}</p> : null}

          {!insightLoading && !aiBusy && !hasPortalAi && !aiError ? (
            <p className="dem-cloudtalk-detail__ai-empty">
              {canPlayRecording
                ? primaryCallId
                  ? 'Waiting to generate transcript from recording…'
                  : 'This row has no CloudTalk call id, so transcript cannot be generated.'
                : 'No transcript saved for this call yet. Management / IT can generate one from the recording.'}
            </p>
          ) : null}

          {summary ? (
            <div className="dem-cloudtalk-detail__remarks">
              <span className="dem-cloudtalk-detail-field__label">Summary</span>
              <div className="dem-cloudtalk-detail__remarks-box">{summary}</div>
            </div>
          ) : null}

          {transcript ? (
            <div className="dem-cloudtalk-detail__remarks">
              <span className="dem-cloudtalk-detail-field__label">Transcript</span>
              <div className="dem-cloudtalk-detail__remarks-box dem-cloudtalk-detail__transcript">
                {transcript}
              </div>
            </div>
          ) : null}

          {aiModel ? (
            <p className="dem-cloudtalk-dem-muted dem-cloudtalk-detail__ai-model">
              Model · {aiModel}
            </p>
          ) : null}
        </section>
      </div>
    </Modal>
  );
}
